(() => {
  if (window.AcProductController) {
    window.AcProductController.init();
    return;
  }

  const controllers = new Map();
  const dialogCleanups = new Map();
  const recommendationCleanups = new Map();
  const descriptionImageCleanups = new Map();
  const lifecycle = new AbortController();
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const reducedMotion = () => motionPreference.matches || document.body.dataset.acMotion === 'reduced';
  const rootsWithin = (scope, selector) => [
    ...(scope?.matches?.(selector) ? [scope] : []),
    ...scope?.querySelectorAll?.(selector) || []
  ];

  const createController = (product) => {
    if (!product || controllers.has(product)) return controllers.get(product);
    const form = product.querySelector('[data-ac-ref-product-form]');
    const variantData = product.querySelector('[data-ac-ref-product-variant]');
    const groups = [...product.querySelectorAll('[data-ac-ref-option-group]')];
    const options = [...product.querySelectorAll('[data-ac-ref-option]')];
    const variantInputs = [...product.querySelectorAll('[data-ac-ref-variant-id], [data-ac-installment-variant]')];
    const mainAdd = product.querySelector('[data-ac-ref-product-add]');
    const stickyAdd = product.querySelector('[data-ac-sticky-add]');
    const addButtons = [mainAdd, stickyAdd].filter(Boolean);
    const error = product.querySelector('[data-ac-ref-product-error]');
    const dynamicCheckout = product.querySelector('[data-ac-dynamic-checkout]');
    const sellingPlan = product.querySelector('[data-ac-selling-plan]');
    const inventoryNodes = [...product.querySelectorAll('[data-ac-ref-product-availability], [data-ac-sticky-availability]')];
    const inventorySource = product.querySelector('[data-ac-ref-product-availability]');
    const sku = product.querySelector('[data-ac-product-sku]');
    const skuValue = product.querySelector('[data-ac-product-sku-value]');
    const sticky = product.querySelector('[data-ac-product-sticky]');
    const stickyVariant = product.querySelector('[data-ac-sticky-variant]');
    const purchase = product.querySelector('[data-ac-product-purchase]');
    const quantityControls = [...product.querySelectorAll('[data-ac-product-quantity-control]')];
    const quantityInputs = quantityControls.map((control) => control.querySelector('[data-ac-product-quantity]')).filter(Boolean);
    const pickup = product.querySelector('pickup-availability');
    const listeners = [];
    let currentVariant = null;
    let variantRequest;
    let variantGeneration = 0;
    let pendingVariant = false;
    let stableChoices = options.filter((input) => input.checked).map((input) => input.id);
    let variant = null;
    let submitting = false;
    let destroyed = false;
    let pickupVariantId = '';
    let focusTimer = 0;
    let stickyFrame = 0;

    try { currentVariant = JSON.parse(variantData?.textContent || 'null'); } catch (error) { currentVariant = null; }

    const copy = (key, fallback = '') => product.dataset[key] || fallback;
    const missingGroup = () => groups.find((group) => !group.querySelector('[data-ac-ref-option]:checked'));
    const resolveVariant = () => {
      if (missingGroup()) return null;
      return currentVariant;
    };
    const selectPrompt = () => missingGroup()?.dataset.acRefOptionKind === 'size'
      ? copy('acTextSelectSize')
      : missingGroup() ? copy('acTextSelectOptions') : copy('acTextUnavailable');
    const buttonText = () => {
      if (submitting) return copy('acTextAdding');
      if (!variant) return selectPrompt();
      return variant.available ? copy('acTextAdd') : copy('acTextSoldOut');
    };
    const allocation = () => variant?.sellingPlans?.find((item) => String(item.id) === String(sellingPlan?.value || '')) || null;

    const updatePrice = () => {
      if (!variant) return;
      const plan = allocation();
      const currentValue = plan?.price ?? variant.price;
      const compareValue = plan?.compareAtPrice ?? variant.compareAtPrice;
      const currentText = plan?.priceFormatted || variant.priceFormatted;
      const compareText = plan?.compareAtPriceFormatted || variant.compareAtPriceFormatted;
      product.querySelectorAll('[data-ac-ref-product-price]').forEach((price) => {
        const current = price.querySelector('[data-ac-ref-product-current-price]');
        const compare = price.querySelector('[data-ac-ref-product-compare-price]');
        const unit = price.querySelector('[data-ac-ref-product-unit-price]');
        if (current) current.textContent = currentText;
        if (compare) {
          compare.textContent = compareText || '';
          compare.hidden = !(compareValue > currentValue);
        }
        if (unit) {
          unit.textContent = variant.unitPriceFormatted || '';
          unit.hidden = !variant.unitPriceFormatted;
        }
        const label = compareValue > currentValue ? price.dataset.saleLabel : price.dataset.regularLabel;
        price.setAttribute('aria-label', label ? `${label}: ${currentText}` : currentText);
      });
    };

    const inventoryText = () => {
      if (!variant) return selectPrompt();
      if (!variant.available) return copy('acTextSoldOut');
      const threshold = Number(inventorySource?.dataset.inventoryThreshold || 0);
      const trackedStock = variant.inventoryManagement === 'shopify' && variant.inventoryQuantity > 0;
      const low = trackedStock && variant.inventoryQuantity <= threshold;
      if (!low) {
        return trackedStock && inventorySource?.dataset.showInventoryQuantity === 'true'
          ? copy('acTextInStockCount', copy('acTextInStock')).replace('[quantity]', variant.inventoryQuantity)
          : copy('acTextInStock');
      }
      return inventorySource?.dataset.showInventoryQuantity === 'true'
        ? copy('acTextLowStockCount').replace('[quantity]', variant.inventoryQuantity)
        : copy('acTextLowStock');
    };

    const normalizeQuantity = (value) => window.AcProductQuantity.normalize(value, variant?.quantityRule);

    const syncQuantity = (value, source) => {
      const rule = variant?.quantityRule || { min: 1, max: null, increment: 1 };
      const normalized = normalizeQuantity(value);
      quantityInputs.forEach((input) => {
        input.min = String(rule.min || 1);
        input.step = String(rule.increment || 1);
        if (rule.max == null) input.removeAttribute('max'); else input.max = String(rule.max);
        if (input !== source || Number(input.value) !== normalized) input.value = String(normalized);
      });
      quantityControls.forEach((control) => {
        const output = control.querySelector('[data-ac-product-quantity-rules]');
        if (!output) return;
        const rules = [];
        if (Number(rule.min) > 1) rules.push(copy('acTextQuantityMin').replace('[quantity]', rule.min));
        if (rule.max != null) rules.push(copy('acTextQuantityMax').replace('[quantity]', rule.max));
        if (Number(rule.increment) > 1) rules.push(copy('acTextQuantityStep').replace('[quantity]', rule.increment));
        output.textContent = rules.join(' · ');
      });
      return normalized;
    };

    const updateOptionAvailability = () => {
      groups.forEach((group) => group.querySelectorAll('[data-ac-ref-option]').forEach((input) => {
        const possible = input.dataset.acOptionAvailable === 'true';
        const label = product.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        if (label) label.dataset.unavailable = String(!possible);
        input.setAttribute('aria-label', possible ? input.value : `${input.value} (${copy('acTextUnavailable')})`);
      }));
    };

    const updateSellingPlans = () => {
      if (!sellingPlan) return;
      const available = new Set((variant?.sellingPlans || []).map((item) => String(item.id)));
      [...sellingPlan.options].forEach((option) => {
        if (!option.value) return;
        option.disabled = !available.has(option.value);
        option.hidden = !available.has(option.value);
      });
      if (sellingPlan.value && !available.has(sellingPlan.value)) sellingPlan.value = '';
      if (product.dataset.acProductRequiresSellingPlan === 'true' && !sellingPlan.value) {
        const first = [...sellingPlan.options].find((option) => option.value && !option.disabled);
        if (first) sellingPlan.value = first.value;
      }
    };

    const updateUrl = () => {
      if (!variant || !window.history?.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.delete('option_values');
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({ variantId: variant.id }, '', url);
    };

    const render = ({ history = false } = {}) => {
      if (destroyed || pendingVariant) return;
      variant = resolveVariant();
      updateOptionAvailability();
      updateSellingPlans();
      variantInputs.forEach((input) => {
        const value = String(variant?.id || '');
        const changed = input.value !== value;
        input.value = value;
        if (input.matches('[data-ac-ref-variant-id]')) input.disabled = !variant;
        if (changed) input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      addButtons.forEach((button) => {
        const label = button.querySelector('[data-ac-product-add-label]');
        (label || button).textContent = buttonText();
        button.disabled = submitting || Boolean(variant && !variant.available);
      });
      if (mainAdd && !variant) mainAdd.disabled = true;
      if (dynamicCheckout) {
        dynamicCheckout.hidden = !variant?.available;
        dynamicCheckout.inert = !variant?.available;
      }
      updatePrice();
      const stock = inventoryText();
      inventoryNodes.forEach((node) => {
        node.textContent = stock;
        node.dataset.available = String(Boolean(variant?.available));
      });
      if (sku && skuValue) {
        skuValue.textContent = variant?.sku || '';
        sku.hidden = !variant?.sku;
      }
      if (stickyVariant) stickyVariant.textContent = variant?.title || selectPrompt();
      syncQuantity(quantityInputs[0]?.value || variant?.quantityRule?.min || 1);
      if (pickup && variant && pickupVariantId !== String(variant.id)) {
        pickupVariantId = String(variant.id);
        pickup.dataset.variantId = String(variant.id);
        pickup.update?.(variant);
      } else if (pickup && !variant) {
        pickupVariantId = '';
        pickup.update?.(null);
      }
      if (variant) {
        const featuredMediaId = variant.featuredMediaId || '';
        const wishlist = product.querySelector('[data-ac-wishlist-toggle]');
        if (wishlist) {
          wishlist.dataset.acWishlistId = product.dataset.acCommerceProductId || '';
          wishlist.dataset.acWishlistProductId = product.dataset.acCommerceProductId || '';
          wishlist.dataset.acWishlistVariantId = String(variant.id);
          wishlist.dataset.acWishlistTitle = product.dataset.acCommerceProductTitle || '';
          wishlist.dataset.acWishlistVariantTitle = variant.title || '';
          wishlist.dataset.acWishlistOptionNames = product.dataset.acCommerceProductOptions || '[]';
          wishlist.dataset.acWishlistOptionValues = JSON.stringify(variant.options || []);
          wishlist.dataset.acWishlistUrl = variant.url || product.dataset.acProductUrl || window.location.pathname;
          wishlist.dataset.acWishlistImage = variant.featuredImage || product.dataset.acCommerceProductImage || '';
          wishlist.dataset.acWishlistPrice = variant.priceFormatted;
          wishlist.dataset.acWishlistAvailable = String(Boolean(variant.available));
          document.dispatchEvent(new CustomEvent('ac:wishlist:preview', { detail: { button: wishlist } }));
        }
        product.dataset.acActiveVariantMediaId = String(featuredMediaId);
        product.dispatchEvent(new CustomEvent('ac:variant-change', { detail: { variant, featuredMediaId } }));
        if (history) updateUrl();
      }
    };

    const focusMissing = () => {
      const group = missingGroup();
      if (!group) return false;
      group.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });
      window.clearTimeout(focusTimer);
      focusTimer = window.setTimeout(() => {
        focusTimer = 0;
        if (!destroyed && group.isConnected) group.querySelector('[data-ac-ref-option]')?.focus({ preventScroll: true });
      }, reducedMotion() ? 0 : 350);
      return true;
    };

    const submit = async (event) => {
      event.preventDefault();
      if (!variant) { focusMissing(); return; }
      if (!variant.available || submitting || pendingVariant || !form) return;
      submitting = true;
      if (error) error.textContent = '';
      render();
      try {
        if (!window.AcCartActions) throw new Error(copy('acTextError'));
        await window.AcCartActions.addAndOpen(form);
      } catch (requestError) {
        if (!destroyed && error) {
          error.textContent = requestError.message || copy('acTextError');
          error.focus({ preventScroll: true });
        }
      } finally {
        submitting = false;
        render();
      }
    };

    const stickySubmit = () => {
      if (!variant) { focusMissing(); return; }
      if (!pendingVariant && variant.available) form?.requestSubmit(mainAdd);
    };
    const share = async (event) => {
      const button = event.currentTarget;
      const url = button.dataset.shareUrl || window.location.href;
      try {
        if (navigator.share) await navigator.share({ title: document.title, url });
        else {
          await navigator.clipboard.writeText(url);
          const status = product.querySelector('[data-ac-product-share-status]');
          if (!destroyed && status) status.textContent = copy('acTextShareCopied');
        }
      } catch (error) { /* Cancelling a share is expected. */ }
    };
    const overlayOpen = () => Boolean(window.AcLayerManager?.isLocked() || document.querySelector('dialog[open], product-modal[open]'));
    let stickyHeight = 0;
    let stickyVisible = false;
    let stickyInitialized = false;
    const updateStickyClearance = () => {
      const value = (stickyVisible ? stickyHeight : 0) + 'px';
      if (product.style.getPropertyValue('--ac-product-sticky-clearance') !== value)
        product.style.setProperty('--ac-product-sticky-clearance', value);
    };
    const stickyObserver = sticky && 'ResizeObserver' in window ? new ResizeObserver(([entry]) => {
      if (destroyed) return;
      stickyHeight = entry.borderBoxSize?.[0]?.blockSize ?? entry.borderBoxSize?.blockSize ?? sticky.offsetHeight;
      updateStickyClearance();
    }) : null;
    stickyObserver?.observe(sticky);

    const updateSticky = (event) => {
      if (destroyed || !sticky || !purchase) return;
      const refreshHeight = !stickyObserver && (!event || event.type === 'resize');
      if (refreshHeight) stickyHeight = 0;
      const visible = purchase.getBoundingClientRect().bottom < 0 && !overlayOpen();
      if (stickyInitialized && visible === stickyVisible && !refreshHeight) return;
      stickyInitialized = true;
      sticky.hidden = false;
      sticky.inert = !visible;
      sticky.classList.toggle('is-visible', visible);
      sticky.setAttribute('aria-hidden', String(!visible));
      stickyVisible = visible;
      if (visible && !stickyHeight) stickyHeight = sticky.offsetHeight;
      updateStickyClearance();
    };

    const setVariantPending = (pending) => {
      pendingVariant = pending;
      product.toggleAttribute('aria-busy', pending);
      if (pending) {
        product.setAttribute('aria-busy', 'true');
        addButtons.forEach((button) => { button.disabled = true; });
        variantInputs.forEach((input) => { if (input.matches('[data-ac-ref-variant-id]')) input.disabled = true; });
        if (dynamicCheckout) dynamicCheckout.inert = true;
        pickupVariantId = '';
        pickup?.update?.(null);
      }
    };

    const loadVariant = async (input) => {
      const generation = ++variantGeneration;
      variantRequest?.abort();
      variantRequest = new AbortController();
      const values = groups.map((group) => group.querySelector('[data-ac-ref-option]:checked')
        || group.querySelector('[data-ac-option-selected="true"]') || group.querySelector('[data-ac-ref-option]'));
      const url = new URL(input.dataset.productUrl || product.dataset.acProductUrl, window.location.href);
      url.searchParams.delete('variant');
      url.searchParams.set('option_values', values.map((option) => option.dataset.optionValueId).join(','));
      const currentUrl = new URL(product.dataset.acProductUrl, window.location.href);
      setVariantPending(true);
      if (url.pathname !== currentUrl.pathname) {
        window.location.assign(url.href);
        return;
      }
      url.searchParams.set('section_id', product.dataset.acSectionId);
      if (error) error.textContent = '';
      try {
        const response = await fetch(url, { headers: { Accept: 'text/html' }, signal: variantRequest.signal });
        if (!response.ok) throw new Error('Variant response unavailable');
        const html = await response.text();
        if (destroyed || generation !== variantGeneration || !product.isConnected) return;
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ac-ref-product]');
        const data = fresh?.querySelector('[data-ac-ref-product-variant]');
        if (!data || fresh.dataset.acCommerceProductId !== product.dataset.acCommerceProductId) throw new Error('Invalid variant response');
        const nextVariant = JSON.parse(data.textContent);
        if (nextVariant && (!nextVariant.id || !Array.isArray(nextVariant.options)
          || nextVariant.options.some((value, index) => value !== values[index]?.value))) throw new Error('Mismatched variant response');
        const nextOptions = [...fresh.querySelectorAll('[data-ac-ref-option]')];
        const replacements = options.map((option) => nextOptions.find((next) => next.dataset.optionValueId === option.dataset.optionValueId));
        if (replacements.some((option) => !option)) throw new Error('Incomplete option response');
        options.forEach((option, index) => {
          option.dataset.acOptionAvailable = replacements[index].dataset.acOptionAvailable;
          option.dataset.acOptionSelected = replacements[index].dataset.acOptionSelected;
          option.dataset.productUrl = replacements[index].dataset.productUrl;
        });
        currentVariant = nextVariant;
        variantData.textContent = data.textContent;
        stableChoices = options.filter((option) => option.checked).map((option) => option.id);
        setVariantPending(false);
        render({ history: true });
      } catch (requestError) {
        if (destroyed || generation !== variantGeneration || requestError.name === 'AbortError') return;
        options.forEach((option) => { option.checked = stableChoices.includes(option.id); });
        setVariantPending(false);
        render();
        window.AcLottieManager?.refresh(product);
        if (error) error.textContent = copy('acTextOptionError');
      } finally {
        if (!destroyed && generation === variantGeneration) setVariantPending(false);
      }
    };

    options.forEach((input) => {
      const change = () => loadVariant(input);
      input.addEventListener('change', change);
      listeners.push(() => input.removeEventListener('change', change));
    });
    listeners.push(window.AcProductQuantity.bind(quantityControls, syncQuantity));
    const shareButtons = [...product.querySelectorAll('[data-ac-product-share]')];
    form?.addEventListener('submit', submit);
    stickyAdd?.addEventListener('click', stickySubmit);
    sellingPlan?.addEventListener('change', render);
    shareButtons.forEach((button) => button.addEventListener('click', share));
    const scheduleStickyUpdate = () => {
      if (stickyFrame) return;
      stickyFrame = requestAnimationFrame(() => { stickyFrame = 0; updateSticky(); });
    };
    window.addEventListener('scroll', scheduleStickyUpdate, { passive: true });
    window.addEventListener('resize', scheduleStickyUpdate, { passive: true });
    document.addEventListener('ac:overlay-change', scheduleStickyUpdate);
    document.addEventListener('ac:layer:change', scheduleStickyUpdate);
    document.addEventListener('click', scheduleStickyUpdate);
    document.body.addEventListener('modalClosed', scheduleStickyUpdate);
    listeners.push(() => {
      form?.removeEventListener('submit', submit);
      stickyAdd?.removeEventListener('click', stickySubmit);
      sellingPlan?.removeEventListener('change', render);
      shareButtons.forEach((button) => button.removeEventListener('click', share));
      stickyObserver?.disconnect();
      window.clearTimeout(focusTimer);
      window.cancelAnimationFrame(stickyFrame);
      product.style.removeProperty('--ac-product-sticky-clearance');
      window.removeEventListener('scroll', scheduleStickyUpdate);
      window.removeEventListener('resize', scheduleStickyUpdate);
      document.removeEventListener('ac:overlay-change', scheduleStickyUpdate);
      document.removeEventListener('ac:layer:change', scheduleStickyUpdate);
      document.removeEventListener('click', scheduleStickyUpdate);
      document.body.removeEventListener('modalClosed', scheduleStickyUpdate);
    });

    const finishInfoReveal = (event) => {
      if (event.animationName === 'ac-product-info-reveal') event.target.dataset.acProductInfoRevealed = '';
    };
    product.addEventListener('animationend', finishInfoReveal);
    product.addEventListener('animationcancel', finishInfoReveal);
    listeners.push(() => {
      product.removeEventListener('animationend', finishInfoReveal);
      product.removeEventListener('animationcancel', finishInfoReveal);
    });
    const refresh = () => { render(); scheduleStickyUpdate(); };
    const destroy = () => {
      if (destroyed) return;
      destroyed = true;
      variantGeneration++;
      variantRequest?.abort();
      if (pendingVariant) options.forEach((option) => { option.checked = stableChoices.includes(option.id); });
      product.removeAttribute('aria-busy');
      listeners.forEach((remove) => remove());
      controllers.delete(product);
    };
    const controller = { refresh, destroy };
    controllers.set(product, controller);
    refresh();
    return controller;
  };

  const initSizeGuide = (scope = document) => {
    const product = scope?.matches?.('[data-ac-ref-product]') ? scope : scope?.querySelector?.('[data-ac-ref-product]');
    const dialog = product?.querySelector('[data-ac-commerce-size-guide]');
    if (!dialog || dialogCleanups.has(dialog)) return;
    const openers = [...product.querySelectorAll('[data-ac-commerce-size-guide-open]')];
    const closeButton = dialog.querySelector('[data-ac-commerce-size-guide-close]');
    const inner = dialog.querySelector('.ac-commerce-size-guide__inner');
    const layerId = dialog.id;
    let openFrame = 0;
    let closing = false;
    let engaged = false;
    let destroyed = false;
    const motionFinished = () => Promise.allSettled(inner.getAnimations().filter((animation) => animation.transitionProperty === 'transform').map((animation) => animation.finished));
    const notify = () => document.dispatchEvent(new CustomEvent('ac:overlay-change'));
    const finish = (restoreFocus = true) => {
      if (!engaged) return;
      engaged = false;
      closing = false;
      window.cancelAnimationFrame(openFrame);
      openFrame = 0;
      dialog.classList.remove('is-active');
      openers.forEach((control) => control.setAttribute('aria-expanded', 'false'));
      window.AcLayerManager?.release(layerId, { restoreFocus });
      notify();
    };

    const lengthColumns = new Set(['length', 'bodylength', 'garmentlength', 'shoulder', 'shoulderwidth', 'chest', 'bust', 'waist', 'hip', 'hips', 'sleeve', 'sleevelength', 'cuff', 'legopening', 'thigh', 'inseam', 'outseam', 'rise', 'frontrise', 'backrise', 'hem', 'height', 'width', '衣长', '裤长', '肩宽', '胸围', '腰围', '臀围', '袖长', '袖口', '裤脚', '腿围']);
    const measurements = [];
    for (const table of dialog.querySelectorAll('[data-ac-size-chart-cm]')) {
      const headers = [...table.querySelectorAll('thead th')];
      headers.forEach((header, index) => {
        const key = header.textContent.toLowerCase().replace(/\([^)]*\)|\[[^\]]*\]|\b(?:cm|centimeters|centimetres)\b/g, '').replace(/[\s_-]+/g, '');
        if (!lengthColumns.has(key)) return;
        for (const row of table.tBodies[0]?.rows || []) {
          const cell = row.cells[index];
          const raw = cell?.textContent.trim();
          if (!raw || !/^\d+(?:[.,]\d+)?(?:\s*(?:-|–|—|~|至|to)\s*\d+(?:[.,]\d+)?)?(?:\s*cm)?$/i.test(raw)) continue;
          measurements.push({ cell, cm: raw, inches: raw.replace(/\d+(?:[.,]\d+)?/g, (number) => (Number(number.replace(',', '.')) / 2.54).toFixed(1)).replace(/\s*cm$/i, '') });
        }
        measurements.push({ cell: header, cm: header.textContent, inches: header.textContent.replace(/\bcm\b/gi, 'in') });
      });
    }
    const units = dialog.querySelector('[data-ac-size-units]');
    if (units && measurements.some(({ cell }) => cell.tagName === 'TD')) units.hidden = false;
    const changeUnit = (event) => {
      const button = event.target.closest('[data-ac-size-unit]');
      if (!button || !units.contains(button)) return;
      const inches = button.dataset.acSizeUnit === 'in';
      measurements.forEach(({ cell, cm, inches: converted }) => { cell.textContent = inches ? converted : cm; });
      units.querySelectorAll('[data-ac-size-unit]').forEach((control) => control.setAttribute('aria-pressed', String(control === button)));
      const note = dialog.querySelector('[data-ac-size-unit-note]');
      if (note) note.textContent = inches ? note.dataset.inLabel : note.dataset.cmLabel;
    };
    units?.addEventListener('click', changeUnit);
    const open = (button) => {
      if (destroyed || dialog.open) return;
      closing = false;
      dialog.classList.toggle('is-reduced-motion', reducedMotion());
      dialog.showModal();
      engaged = true;
      window.AcLayerManager?.acquire(layerId, {
        opener: button,
        getFocusRoots: () => [],
        getInertRoots: () => [],
        onEscape: () => close(),
      });
      openers.forEach((control) => control.setAttribute('aria-expanded', String(control === button)));
      inner.getBoundingClientRect();
      openFrame = requestAnimationFrame(() => {
        openFrame = 0;
        if (destroyed || closing || !dialog.open || !dialog.isConnected) return;
        dialog.classList.add('is-active');
        motionFinished().then(() => {
          if (!destroyed && !closing && dialog.open && document.documentElement.dataset.acInputMode === 'keyboard') closeButton?.focus({ preventScroll: true });
        });
        notify();
      });
    };
    const close = () => {
      if (!dialog.open || closing) return;
      closing = true;
      window.cancelAnimationFrame(openFrame);
      openFrame = 0;
      dialog.classList.toggle('is-reduced-motion', reducedMotion());
      dialog.classList.remove('is-active');
      motionFinished().then(() => {
        if (destroyed || !closing) return;
        dialog.close();
        finish();
      });
    };
    const openerHandlers = openers.map((button) => {
      const handler = () => open(button);
      button.addEventListener('click', handler);
      return () => button.removeEventListener('click', handler);
    });
    const cancel = (event) => { event.preventDefault(); close(); };
    const backdrop = (event) => { if (event.target === dialog) close(); };
    const nativeClose = () => { if (!dialog.open) finish(); };
    closeButton?.addEventListener('click', close);
    dialog.addEventListener('cancel', cancel);
    dialog.addEventListener('click', backdrop);
    dialog.addEventListener('close', nativeClose);
    const destroy = () => {
      if (destroyed) return;
      destroyed = true;
      window.cancelAnimationFrame(openFrame);
      openerHandlers.forEach((remove) => remove());
      closeButton?.removeEventListener('click', close);
      dialog.removeEventListener('cancel', cancel);
      dialog.removeEventListener('click', backdrop);
      dialog.removeEventListener('close', nativeClose);
      units?.removeEventListener('click', changeUnit);
      if (dialog.open) dialog.close();
      finish(false);
      dialogCleanups.delete(dialog);
    };
    dialogCleanups.set(dialog, destroy);
    return destroy;
  };

  // Both related and recent products wait on a stable, visible-size marker.
  // Their own roots may be empty or hidden and cannot be observed reliably.
  const whenNearProductEnd = (scope, callback) => {
    const anchor = scope.closest('.shopify-section')?.querySelector('[data-ac-product-load-anchor]');
    let observer;
    let done = false;
    const run = () => {
      if (done) return;
      done = true;
      observer?.disconnect();
      callback();
    };
    if (anchor && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) run();
      }, { rootMargin: `${Math.ceil(window.innerHeight)}px 0px` });
      observer.observe(anchor);
    } else run();
    return () => { done = true; observer?.disconnect(); };
  };

  const initRecommendations = (scope = document) => {
    const roots = rootsWithin(scope, '[data-ac-product-recommendations]');
    roots.forEach((root) => {
      if (root.dataset.acRecommendationsBound === 'true' || !root.dataset.url) return;
      root.dataset.acRecommendationsBound = 'true';
      root.setAttribute('aria-busy', 'true');
      const abortController = new AbortController();
      let idleId = 0;
      let timerId = 0;
      let cancelNear;
      const load = async () => {
        if (abortController.signal.aborted) return;
        try {
          const response = await fetch(root.dataset.url, {
            headers: { Accept: 'text/html' },
            signal: abortController.signal
          });
          if (!response.ok) throw new Error('Recommendations unavailable');
          const html = await response.text();
          if (abortController.signal.aborted || !root.isConnected) return;
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const selector = `[data-ac-product-recommendations][data-intent="${CSS.escape(root.dataset.intent || '')}"]`;
          const replacement = documentFragment.querySelector(selector);
          if (!replacement?.innerHTML.trim()) {
            root.hidden = true;
            return;
          }
          root.innerHTML = replacement.innerHTML;
          root.hidden = false;
          document.dispatchEvent(new CustomEvent('ac:recommendations-loaded', { detail: { root } }));
        } catch (error) {
          if (!abortController.signal.aborted && error.name !== 'AbortError') root.hidden = true;
        } finally {
          if (!abortController.signal.aborted) root.removeAttribute('aria-busy');
        }
      };
      const destroy = () => {
        cancelNear?.();
        abortController.abort();
        if (idleId) window.cancelIdleCallback?.(idleId);
        if (timerId) window.clearTimeout(timerId);
        delete root.dataset.acRecommendationsBound;
        root.removeAttribute('aria-busy');
        recommendationCleanups.delete(root);
      };
      recommendationCleanups.set(root, destroy);
      if (root.dataset.intent === 'related') cancelNear = whenNearProductEnd(root, load);
      else if ('requestIdleCallback' in window) idleId = window.requestIdleCallback(load, { timeout: 1200 });
      else timerId = window.setTimeout(load, 32);
    });
  };

  const initDescriptionImages = (product) => {
    rootsWithin(product, '[data-ac-description-images]').forEach((root) => {
      if (descriptionImageCleanups.has(root)) return;
      const details = root.closest('details');
      const template = root.querySelector('template');
      if (!details || !template) return;
      let ready = document.readyState === 'complete';
      let loaded = false;
      const loadingNote = root.querySelector('[data-ac-description-images-loading]');
      if (loadingNote) loadingNote.hidden = false;
      const reveal = (opening = false) => {
        if (!ready || loaded || (!opening && !details.open)) return;
        loaded = true;
        root.querySelector('[data-ac-description-images-loading]')?.remove();
        root.append(template.content.cloneNode(true));
        template.remove();
      };
      // Hydrate before the shared disclosure measures its opening animation.
      const onClick = (event) => {
        if (event.target.closest('summary')?.parentElement === details && details.dataset.acDisclosureTarget !== 'open') reveal(true);
      };
      const onToggle = () => reveal();
      const onLoad = () => { ready = true; reveal(); };
      details.addEventListener('click', onClick);
      details.addEventListener('toggle', onToggle);
      if (!ready) window.addEventListener('load', onLoad, { once: true });
      reveal();
      descriptionImageCleanups.set(root, () => {
        details.removeEventListener('click', onClick);
        details.removeEventListener('toggle', onToggle);
        window.removeEventListener('load', onLoad);
        descriptionImageCleanups.delete(root);
      });
    });
  };

  const init = (scope = document) => {
    rootsWithin(scope, '[data-ac-ref-product]').forEach((product) => {
      createController(product);
      initSizeGuide(product);
      initDescriptionImages(product);
    });
    initRecommendations(scope);
  };
  const destroyWithin = (scope) => {
    for (const [root, controller] of controllers) {
      if (root === scope || scope.contains(root)) controller.destroy();
    }
    for (const cleanups of [dialogCleanups, recommendationCleanups, descriptionImageCleanups]) {
      for (const [root, cleanup] of cleanups) {
        if (root === scope || scope.contains(root)) cleanup();
      }
    }
  };
  const destroy = () => {
    lifecycle.abort();
    controllers.forEach((controller) => controller.destroy());
    for (const cleanups of [dialogCleanups, recommendationCleanups, descriptionImageCleanups]) cleanups.forEach((cleanup) => cleanup());
    delete window.AcProductController;
  };
  window.AcProductController = { init, destroyWithin, destroy, whenNearProductEnd };
  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target), { signal: lifecycle.signal });
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target), { signal: lifecycle.signal });
  window.addEventListener('pagehide', (event) => {
    if (!event.persisted) destroy();
  }, { signal: lifecycle.signal });
})();
